/**
 * <s-boutique> — walk-into-the-shop scenes. Listens for the `--enter` and
 * `--exit` invoker commands and moves `data-state` between `outside`,
 * `entering` and `inside`; CSS in the boutique block runs the glide. Keeps
 * the picture centered on screens narrower than the photo, hides whichever
 * scene is not in view from assistive tech, and moves focus into the new scene.
 */
class SBoutique extends HTMLElement {
  #ac;
  #timer;
  #clock;

  connectedCallback() {
    this.#ac = new AbortController();
    this.addEventListener('command', (event) => {
      if (event.command === '--enter') this.#enter();
      if (event.command === '--exit') this.#exit();
    }, { signal: this.#ac.signal });
    window.addEventListener('resize', () => this.#center(), { signal: this.#ac.signal });
    this.#center();
    this.#sync();
    this.#updateTime();
    this.#clock = setInterval(() => this.#updateTime(), 60000);
    if (this.hasAttribute('data-instant')) {
      const url = new URL(window.location.href);
      url.searchParams.delete('shop');
      url.hash = '';
      history.replaceState(history.state, '', url.pathname + url.search);
      requestAnimationFrame(() => requestAnimationFrame(() => delete this.dataset.instant));
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#timer);
    clearInterval(this.#clock);
  }

  /** Day or night by the shopper's own clock (sunrise and sunset approximated by month); ?time=night or ?time=day previews either. */
  #updateTime() {
    const now = new Date();
    const sunrise = [7.25, 7, 7, 6.5, 6, 5.75, 5.75, 6.25, 6.75, 7.25, 6.75, 7.25][now.getMonth()];
    const sunset = [17.25, 17.75, 19, 19.5, 20, 20.5, 20.5, 20, 19.25, 18.5, 17, 16.75][now.getMonth()];
    const hour = now.getHours() + now.getMinutes() / 60;
    let isNight = !(hour >= sunrise && hour < sunset);
    const time = new URLSearchParams(window.location.search).get('time');
    if (time === 'night') isNight = true;
    if (time === 'day') isNight = false;
    this.dataset.time = isNight ? 'night' : 'day';
    for (const nightImage of this.querySelectorAll('.boutique-night')) {
      if (nightImage.hidden !== !isNight) nightImage.hidden = !isNight;
    }
  }

  #center() {
    const pan = this.querySelector('[data-boutique-pan]');
    if (pan) pan.scrollLeft = (pan.scrollWidth - pan.clientWidth) / 2;
  }

  #reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #enter() {
    if (this.dataset.state !== 'outside') return;
    clearTimeout(this.#timer);
    this.dataset.state = 'entering';
    this.#sync();
    this.#timer = setTimeout(() => {
      this.dataset.state = 'inside';
      this.#sync();
      this.#center();
      this.querySelector('[data-boutique-inside] a, [data-boutique-inside] button')?.focus({ preventScroll: true });
      // Settle the tablet's starting position after the inside layout and focus update.
      if (window.matchMedia('(orientation: portrait) and (min-width: 768px) and (max-width: 1366px) and (any-pointer: coarse)').matches) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          if (this.isConnected && this.dataset.state === 'inside') this.#center();
        }));
      }
    }, this.#reduced() ? 0 : 1600);
  }

  #exit() {
    clearTimeout(this.#timer);
    this.dataset.state = 'outside';
    this.#sync();
    this.#center();
    this.querySelector('[data-boutique-outside] button')?.focus({ preventScroll: true });
  }

  #sync() {
    const inside = this.querySelector('[data-boutique-inside]');
    const outside = this.querySelector('[data-boutique-outside]');
    const isOutside = this.dataset.state === 'outside';
    if (inside) inside.inert = isOutside;
    if (outside) outside.inert = !isOutside;
    for (const control of this.querySelectorAll('.boutique-inside-only')) control.inert = isOutside;
  }
}

if (!customElements.get('s-boutique')) customElements.define('s-boutique', SBoutique);
